import { useId, useState } from 'react';

import type { EmailDetail } from '@/shared/emails/types';
import { useCorrectTriage } from '@/shared/emails/useEmails';
import { CATEGORY_LABELS, categoryChoices, type EmailCategory } from '@/shared/triage/vocabulary';
import { Button } from '@/shared/ui/Button';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { Input, Select } from '@/shared/ui/Input';
import { cn } from '@/shared/utils/cn';

/** What an email read as an offer most often turned out to be, in production: an order
 * confirmation from a supplier, which is `gestion_interna` since 2026-09-28. */
const MOST_LIKELY: EmailCategory = 'gestion_interna';

/**
 * «Esto no es una oferta de proveedor», on a draft that came from an email (contract
 * v10.24).
 *
 * The demo of 2026-09-24: an order confirmation from a supplier's agent was read as an
 * offer and left this very draft. Presupuestos is where the mistake is seen, so it is
 * where it is corrected: the same correction as «Corregir…» in the inbox, with the
 * category the person says it is. The back then cancels this draft (it has no number)
 * and records the answer for the eval of the week of monitoring. Where it goes is the
 * mailbox's own table (`destination_by_category`), not a guess of this screen.
 */
export function NotAnOffer({ email, className }: { email: EmailDetail; className?: string }) {
  const [category, setCategory] = useState<EmailCategory>(MOST_LIKELY);
  const [note, setNote] = useState('');
  const correct = useCorrectTriage(email.id);
  const id = useId();
  const destination = email.triage_plan?.destination_by_category?.[category] ?? 'se_queda';

  return (
    <details className={cn('group text-[13.5px]', className)}>
      <summary className="text-ink-2 hover:text-ink cursor-pointer list-none select-none [&::-webkit-details-marker]:hidden">
        ¿Esto no es una oferta de proveedor?
      </summary>
      <div className="mt-2 flex flex-col gap-2">
        <p className="text-ink-3 text-[13px]">
          Dilo aquí: el correo se corrige, este borrador se anula y la corrección sirve para afinar
          la clasificación.
        </p>
        <Select
          id={`${id}-que`}
          label="¿Qué es?"
          value={category}
          onChange={(event) => setCategory(event.target.value as EmailCategory)}
        >
          {categoryChoices()
            .filter((each) => each !== 'oferta_proveedor_respuesta')
            .map((each) => (
              <option key={each} value={each}>
                {CATEGORY_LABELS[each]}
              </option>
            ))}
        </Select>
        <Input
          id={`${id}-nota`}
          label="¿Por qué? (opcional)"
          value={note}
          maxLength={500}
          onChange={(event) => setNote(event.target.value)}
        />
        <div>
          <Button
            variant="secondary"
            disabled={correct.isPending || correct.isSuccess}
            onClick={() =>
              correct.mutate({
                final_destination: destination,
                category,
                note: note.trim() || null,
              })
            }
          >
            {correct.isPending ? 'Guardando…' : 'No es una oferta: anular el borrador'}
          </Button>
        </div>
        {correct.isError ? (
          <ErrorMessage error={correct.error}>No se pudo guardar</ErrorMessage>
        ) : null}
        <p role="status" aria-live="polite" className="text-ok text-sm">
          {correct.isSuccess
            ? `Guardado: es «${CATEGORY_LABELS[category]}». El borrador queda anulado.`
            : ''}
        </p>
      </div>
    </details>
  );
}
